import React from 'react'
import { useExitIntent } from 'use-exit-intent'

import DidYouBookTheMeetingModal from '@/components/misc/RequestCompanyCallbackButton/DidYouBookTheMeetingModal'
import SafeBookingIframe from '@/components/misc/SafeBookingIframe'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Heading } from '@/components/ui/heading'
import useDialog from '@/hooks/useDialog'

interface Props {
  companyName: string
  scheduleCallUrl: string
  onBooked: () => void
  onHide: () => void
  isProcessing: boolean
}

const NonCalendlyModal: React.FC<Props> = ({
  companyName,
  scheduleCallUrl,
  onBooked,
  onHide,
  isProcessing,
}) => {
  const {
    isShowingDialog: isShowingExitPromptDialog,
    showDialog: showExitPromptDialog,
    hideDialog: hideExitPromptDialog,
  } = useDialog()

  const { registerHandler, isTriggered: hasExitPromptBeenShown } =
    useExitIntent({
      desktop: {
        delayInSecondsToTrigger: 60,
        triggerOnMouseLeave: true,
        triggerOnIdle: true,
        useBeforeUnload: false,
      },
      mobile: {
        triggerOnIdle: true,
        delayInSecondsToTrigger: 60,
      },
    })

  // This will show the modal when user is about to leave the page or is idle
  // for 60 seconds as configured via the useExitIntent hook above
  registerHandler({
    id: 'openModal',
    handler: () => {
      showExitPromptDialog()
    },
  })

  function handleHide() {
    // If the user has answered the exit prompt already, hide the modal
    if (hasExitPromptBeenShown) {
      onHide()
    } else {
      // Otherwise, show the exit prompt modal
      showExitPromptDialog()
    }
  }

  return (
    <>
      <Dialog open onHide={handleHide}>
        {isShowingExitPromptDialog && (
          <div
            className="absolute top-0 right-0 bottom-0 left-0 z-1000 w-full
              bg-black opacity-[0.6]"
          />
        )}

        <DialogContent>
          <DialogHeader>
            <DialogTitle>Schedule a meeting</DialogTitle>
          </DialogHeader>

          <DialogBody>
            <Heading size={2}>{companyName}</Heading>

            <SafeBookingIframe
              title={`Schedule call with ${companyName}`}
              url={scheduleCallUrl}
              height="600px"
            />
          </DialogBody>
        </DialogContent>
      </Dialog>

      {isShowingExitPromptDialog && (
        <DidYouBookTheMeetingModal
          companyName={companyName}
          onBooked={onBooked}
          onNoBooked={() => {
            hideExitPromptDialog()
            onHide()
          }}
          onHide={hideExitPromptDialog}
          isProcessing={isProcessing}
        />
      )}
    </>
  )
}

export default NonCalendlyModal
